const CAPITAL_PREFIX = '01';
const CAPITAL_START = 1;
const CAPITAL_END = 13;
const OPERATIONAL_PREFIX = '09';
const OPERATIONAL_START = 1;
const OPERATIONAL_END = 45;

const pad2 = (value) => String(value).padStart(2, '0');

const parseCodePart = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseCodeTuple = (codeNumber = '') => {
  const raw = String(codeNumber || '').trim();
  if (!raw) {
    return { first: null, second: null, third: null };
  }

  const pieces = raw.match(/\d+/g) || [];
  return {
    first: parseCodePart(pieces[0]),
    second: parseCodePart(pieces[1]),
    third: parseCodePart(pieces[2])
  };
};

const parseCodeParts = (codeNumber = '') => {
  const raw = String(codeNumber || '').trim();
  if (!raw) {
    return [];
  }

  return (raw.match(/\d+/g) || [])
    .map((value) => parseCodePart(value))
    .filter((value) => value !== null);
};

export const toCanonicalBudgetCode = (codeNumber = '') => {
  const raw = String(codeNumber || '').trim();
  if (!raw) {
    return '';
  }

  const pieces = raw.match(/\d+/g) || [];
  if (pieces.length === 0) {
    return raw;
  }

  const normalizedPieces = pieces
    .map((piece) => parseCodePart(piece))
    .filter((part) => part !== null);

  if (normalizedPieces.length === 0) {
    return raw;
  }

  return normalizedPieces.map((part) => String(part)).join('.');
};

const compareBudgetCodes = (firstCode = '', secondCode = '') => {
  const firstParts = parseCodeParts(firstCode);
  const secondParts = parseCodeParts(secondCode);

  if (firstParts.length === 0 && secondParts.length === 0) {
    return String(firstCode).localeCompare(String(secondCode), undefined, {
      numeric: true,
      sensitivity: 'base'
    });
  }
  if (firstParts.length === 0) {
    return 1;
  }
  if (secondParts.length === 0) {
    return -1;
  }

  const sharedDepth = Math.min(firstParts.length, secondParts.length);
  for (let index = 0; index < sharedDepth; index += 1) {
    if (firstParts[index] !== secondParts[index]) {
      return firstParts[index] - secondParts[index];
    }
  }

  // Parent row should appear before child row:
  // e.g. 09.01 before 09.01.01, 09.02 before 09.02.01.
  if (firstParts.length !== secondParts.length) {
    return firstParts.length - secondParts.length;
  }

  return String(firstCode).localeCompare(String(secondCode), undefined, {
    numeric: true,
    sensitivity: 'base'
  });
};

export const sortBudgetRowsByCode = (rows = [], codeSelector = (row) => row?.code_number || row?.codeNumber || '') => (
  [...(Array.isArray(rows) ? rows : [])].sort((a, b) => {
    const firstCode = codeSelector(a);
    const secondCode = codeSelector(b);
    return compareBudgetCodes(firstCode, secondCode);
  })
);

export const inferBudgetItemTypeFromCode = (codeNumber = '', explicitType = '') => {
  const type = String(explicitType || '').trim().toLowerCase();
  if (type === 'capital' || type === 'operational') {
    return type;
  }

  const canonical = toCanonicalBudgetCode(codeNumber);
  const firstPart = parseCodePart(String(canonical).split('.')[0]);

  if (firstPart === 9 || firstPart === 2) {
    return 'operational';
  }
  return 'capital';
};

const hasBudgetContent = (item = {}) => {
  const numericValue = Number(item?.estimated_budget || 0);
  return Boolean(
    String(item?.code_number || '').trim() ||
    String(item?.expense_title || '').trim() ||
    (Number.isFinite(numericValue) && numericValue !== 0) ||
    String(item?.remarks || '').trim()
  );
};

const buildTemplateRowsFromMasterItems = (masterItems = []) => {
  if (!Array.isArray(masterItems) || masterItems.length === 0) {
    return [];
  }

  const normalizedRows = masterItems
    .map((item, index) => {
      const codeNumber = String(item?.code_number || '').trim();
      const expenseTitle = String(item?.expense_title || '').trim();
      const itemType = inferBudgetItemTypeFromCode(codeNumber, item?.item_type);

      return {
        code_number: codeNumber,
        expense_title: expenseTitle,
        item_type: itemType,
        order_index: Number.isFinite(Number(item?.order_index))
          ? Number(item?.order_index)
          : index
      };
    })
    .filter((row) => row.code_number || row.expense_title);

  if (normalizedRows.length === 0) {
    return [];
  }

  const seenKeys = new Set();
  const dedupedRows = [];
  normalizedRows.forEach((row) => {
    const canonicalCode = toCanonicalBudgetCode(row.code_number);
    const dedupeKey = canonicalCode || `${row.code_number}::${row.expense_title}`;
    if (seenKeys.has(dedupeKey)) {
      return;
    }
    seenKeys.add(dedupeKey);
    dedupedRows.push(row);
  });

  const capitalRows = dedupedRows.filter((row) => row.item_type === 'capital');
  const operationalRows = dedupedRows.filter((row) => row.item_type === 'operational');

  return [...capitalRows, ...operationalRows].map((row) => ({
    code_number: row.code_number,
    expense_title: row.expense_title,
    item_type: row.item_type
  }));
};

export const buildFixedBudgetTemplateRows = (masterItems = []) => {
  const masterTemplateRows = buildTemplateRowsFromMasterItems(masterItems);
  if (masterTemplateRows.length > 0) {
    return masterTemplateRows;
  }

  const formatBudgetCode = (prefix, index) => `${prefix}.${pad2(index)}`;

  const masterMap = new Map();

  if (Array.isArray(masterItems)) {
    masterItems.forEach((item) => {
      const canonical = toCanonicalBudgetCode(item?.code_number || '');
      if (!canonical) {
        return;
      }

      const { first, second } = parseCodeTuple(item?.code_number || '');
      const isCapitalCode = first === 1 && second !== null && second >= CAPITAL_START && second <= CAPITAL_END;
      const isOperationalCode = first === 9 && second !== null && second >= OPERATIONAL_START && second <= OPERATIONAL_END;
      if (!isCapitalCode && !isOperationalCode) {
        return;
      }

      masterMap.set(canonical, {
        expense_title: item?.expense_title || '',
        item_type: inferBudgetItemTypeFromCode(item?.code_number, item?.item_type)
      });
    });
  }

  const capitalRows = [];
  for (let codeIndex = CAPITAL_START; codeIndex <= CAPITAL_END; codeIndex += 1) {
    const codeNumber = formatBudgetCode(CAPITAL_PREFIX, codeIndex);
    const canonical = toCanonicalBudgetCode(codeNumber);
    const mapped = masterMap.get(canonical);
    capitalRows.push({
      code_number: codeNumber,
      expense_title: mapped?.expense_title || '',
      item_type: 'capital'
    });
  }

  const operationalRows = [];
  for (let codeIndex = OPERATIONAL_START; codeIndex <= OPERATIONAL_END; codeIndex += 1) {
    const codeNumber = formatBudgetCode(OPERATIONAL_PREFIX, codeIndex);
    const canonical = toCanonicalBudgetCode(codeNumber);
    const mapped = masterMap.get(canonical);
    operationalRows.push({
      code_number: codeNumber,
      expense_title: mapped?.expense_title || '',
      item_type: 'operational'
    });
  }

  return [...capitalRows, ...operationalRows];
};

export const mergeBudgetItemsWithFixedTemplate = (submissionRows = [], masterItems = []) => {
  const templateRows = buildFixedBudgetTemplateRows(masterItems);
  const normalizedSubmissionRows = Array.isArray(submissionRows) ? submissionRows : [];

  const submissionByCanonicalCode = new Map();
  normalizedSubmissionRows.forEach((row) => {
    const canonical = toCanonicalBudgetCode(row?.code_number || '');
    if (!canonical || submissionByCanonicalCode.has(canonical)) {
      return;
    }
    submissionByCanonicalCode.set(canonical, row);
  });

  const mergedRows = templateRows.map((templateRow) => {
    const canonical = toCanonicalBudgetCode(templateRow.code_number);
    const matched = canonical ? submissionByCanonicalCode.get(canonical) : null;
    return {
      code_number: templateRow.code_number,
      expense_title: templateRow.expense_title || matched?.expense_title || '',
      estimated_budget: matched?.estimated_budget ?? '',
      remarks: matched?.remarks || '',
      item_type: templateRow.item_type || inferBudgetItemTypeFromCode(templateRow.code_number, matched?.item_type)
    };
  });

  const templateCanonicalCodes = new Set(
    templateRows.map((row) => toCanonicalBudgetCode(row.code_number)).filter(Boolean)
  );

  const customRows = normalizedSubmissionRows
    .filter((row) => {
      const canonical = toCanonicalBudgetCode(row?.code_number || '');
      if (!canonical) {
        return hasBudgetContent(row);
      }
      return !templateCanonicalCodes.has(canonical);
    })
    .map((row) => ({
      code_number: row?.code_number || '',
      expense_title: row?.expense_title || '',
      estimated_budget: row?.estimated_budget ?? '',
      remarks: row?.remarks || '',
      item_type: inferBudgetItemTypeFromCode(row?.code_number, row?.item_type)
    }));

  return [...mergedRows, ...customRows];
};
